import { useEffect, useMemo } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { useApprovedTemplates } from '../../../hooks/useWhatsApp.js';
import { getApiErrorMessage } from '../../../lib/api.js';

// Human-readable labels for per-user / computed fields the backend supports.
export const FIELD_LABEL = {
  // Per-user
  full_name: 'Full name',
  first_name: 'First name',
  last_name: 'Last name',
  badge_no: 'Badge no.',
  dgp_id: 'DGP ID',
  designation_name: 'Designation',
  company_name: 'Company',
  phone_number: 'Phone',
  email: 'Email',
  // Computed at send time (same value for every recipient)
  current_month_en: 'Current month — English (e.g. August)',
  current_month_mr: 'Current month — Marathi (e.g. ऑगस्ट)',
  current_month_year_en: 'Current month + year — English (August 2026)',
  current_month_year_mr: 'Current month + year — Marathi (ऑगस्ट 2026)',
  previous_month_en: 'Previous month — English (e.g. July)',
  previous_month_mr: 'Previous month — Marathi (e.g. जुलै)',
  previous_month_year_en: 'Previous month + year — English (July 2026)',
  previous_month_year_mr: 'Previous month + year — Marathi (जुलै 2026)',
  financial_year: 'Financial year (e.g. 2026-27)',
  previous_financial_year: 'Previous financial year (e.g. 2025-26)',
};

const USER_FIELD_SET = new Set([
  'full_name', 'first_name', 'last_name',
  'badge_no', 'dgp_id', 'designation_name', 'company_name',
  'phone_number', 'email',
]);
const COMPUTED_FIELD_SET = new Set([
  'current_month_en', 'current_month_mr',
  'current_month_year_en', 'current_month_year_mr',
  'previous_month_en', 'previous_month_mr',
  'previous_month_year_en', 'previous_month_year_mr',
  'financial_year', 'previous_financial_year',
]);

const EN_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MR_MONTHS = [
  'जानेवारी', 'फेब्रुवारी', 'मार्च', 'एप्रिल', 'मे', 'जून',
  'जुलै', 'ऑगस्ट', 'सप्टेंबर', 'ऑक्टोबर', 'नोव्हेंबर', 'डिसेंबर',
];

// Mirror the backend's computed month values so the preview matches what will
// actually be sent. "previous" = the month before now (a June slip goes out in July).
function computeMonthValue(key) {
  const now = new Date();
  // Indian financial year (Apr–Mar), e.g. "2026-27". Jan–Mar belong to the FY
  // that started the previous calendar year.
  if (key === 'financial_year' || key === 'previous_financial_year') {
    const offset = key === 'previous_financial_year' ? -1 : 0;
    const startYear = (now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1) + offset;
    return `${startYear}-${String(startYear + 1).slice(-2)}`;
  }
  const isPrev = key.startsWith('previous');
  const d = new Date(now.getFullYear(), now.getMonth() - (isPrev ? 1 : 0), 1);
  const marathi = key.endsWith('_mr');
  const name = (marathi ? MR_MONTHS : EN_MONTHS)[d.getMonth()];
  return key.includes('year') ? `${name} ${d.getFullYear()}` : name;
}

/**
 * Render the template body with variables filled in for the preview.
 * - static params → the literal text the admin typed (e.g. the month)
 * - field params  → the real value from `sampleRecipient` when available
 *                   (falls back to a [Label] placeholder), or a computed month
 */
export function renderPreview(bodyText, paramSpecs, sampleRecipient = null) {
  if (!bodyText) return '';
  return bodyText.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, idx) => {
    const spec = paramSpecs[parseInt(idx, 10) - 1];
    if (!spec) return match;
    if (spec.type === 'static') return spec.value || match;
    if (spec.type === 'field') {
      if (COMPUTED_FIELD_SET.has(spec.value)) return computeMonthValue(spec.value);
      const real = sampleRecipient?.[spec.value];
      if (real !== undefined && real !== null && real !== '') return String(real);
      return `[${FIELD_LABEL[spec.value] || spec.value}]`;
    }
    return match;
  });
}

const templateKey = (t) => `${t.name}::${t.language}`;

// Heuristic: look at the ~30 chars around {{n}} in the body and pick a sensible
// default field. Falls back to full_name when nothing matches.
function guessFieldForVariable(bodyText, varNum) {
  if (!bodyText) return 'full_name';
  const re = new RegExp(`\\{\\{\\s*${varNum}\\s*\\}\\}`);
  const m = re.exec(bodyText);
  if (!m) return 'full_name';
  const start = Math.max(0, m.index - 30);
  const end = Math.min(bodyText.length, m.index + m[0].length + 30);
  const ctx = bodyText.slice(start, end).toLowerCase();

  // Month-of-salary-slip context → previous month (slip for Aug is sent in Sep).
  if (/महिन्या|month/.test(ctx)) return 'previous_month_mr';
  if (/badge|बॅज/.test(ctx)) return 'badge_no';
  if (/designation|पद/.test(ctx)) return 'designation_name';
  if (/dgp/.test(ctx)) return 'dgp_id';
  // First variable is almost always the recipient's name.
  if (varNum === 1) return 'full_name';
  return 'full_name';
}

/**
 * Controlled template picker. Renders:
 *   - dropdown of APPROVED Meta templates (auto-fetched)
 *   - body preview with live variable substitution
 *   - per-{{n}} input (field-per-user or static-text)
 *
 * Props:
 *   value:    { selectedKey: string | null, params: Array<{type,value}> }
 *   onChange: (next) => void
 *   compact:  boolean (defaults false; when true uses tighter spacing)
 */
export default function TemplatePicker({ value, onChange, compact = false, sampleRecipient = null }) {
  const { data: resp, isLoading, isFetching, error, refetch } = useApprovedTemplates();
  const templates = resp?.data?.data || [];
  const fieldOptions = resp?.data?.field_options || Object.keys(FIELD_LABEL);

  const selectedTemplate = useMemo(
    () => templates.find((t) => templateKey(t) === value.selectedKey) || null,
    [templates, value.selectedKey]
  );

  // When the selected template changes, reset params to match its variable count.
  // For each {{n}} we try to guess a sensible default by looking at the words
  // around the placeholder — e.g. "महिन्याची" / "month" → previous_month_mr.
  useEffect(() => {
    if (!selectedTemplate) return;
    if (value.params.length === selectedTemplate.param_count) return;
    const body = selectedTemplate.body_text || '';
    const defaults = Array.from({ length: selectedTemplate.param_count }, (_, i) => {
      const guess = guessFieldForVariable(body, i + 1);
      return { type: 'field', value: guess };
    });
    onChange({ ...value, params: defaults });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.selectedKey, selectedTemplate?.param_count]);

  const handleSelect = (e) => {
    onChange({ selectedKey: e.target.value || null, params: [] });
  };

  const updateParam = (idx, partial) => {
    const next = value.params.map((p, i) => (i === idx ? { ...p, ...partial } : p));
    onChange({ ...value, params: next });
  };

  if (error) {
    return (
      <div className="p-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <div>
            <p className="font-semibold">Failed to load templates from Meta</p>
            <p className="mt-1">{getApiErrorMessage(error, 'Unknown error')}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 text-xs underline text-red-700 dark:text-red-300"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {/* Selector */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Approved template *
          </label>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1"
          >
            <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <select
          value={value.selectedKey || ''}
          onChange={handleSelect}
          disabled={isLoading}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-700"
        >
          <option value="">
            {isLoading
              ? 'Loading approved templates…'
              : templates.length === 0
                ? 'No approved templates found in Meta'
                : '— Select a template —'}
          </option>
          {templates.map((t) => (
            <option key={templateKey(t)} value={templateKey(t)}>
              {t.name} ({t.language}){t.category ? ` · ${t.category}` : ''}
              {t.param_count > 0 ? ` · ${t.param_count} var${t.param_count !== 1 ? 's' : ''}` : ''}
            </option>
          ))}
        </select>
      </div>

      {selectedTemplate && (
        <>
          {/* Variable inputs */}
          {selectedTemplate.param_count > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Variables
              </p>
              <div className="space-y-2">
                {value.params.map((spec, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                    <span className="font-mono text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 min-w-[3rem] text-center">
                      {`{{${idx + 1}}}`}
                    </span>
                    <select
                      value={spec.type}
                      onChange={(e) => updateParam(idx, {
                        type: e.target.value,
                        value: e.target.value === 'field' ? 'full_name' : '',
                      })}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                    >
                      <option value="field">Per-user field</option>
                      <option value="static">Static text</option>
                    </select>
                    {spec.type === 'field' ? (
                      <select
                        value={spec.value}
                        onChange={(e) => updateParam(idx, { value: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                      >
                        <optgroup label="Per-user (different for each recipient)">
                          {fieldOptions.filter((f) => USER_FIELD_SET.has(f)).map((f) => (
                            <option key={f} value={f}>{FIELD_LABEL[f] || f}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Computed at send time (same for everyone)">
                          {fieldOptions.filter((f) => COMPUTED_FIELD_SET.has(f)).map((f) => (
                            <option key={f} value={f}>{FIELD_LABEL[f] || f}</option>
                          ))}
                        </optgroup>
                        {/* Any fields the backend exposed that we don't recognise */}
                        {fieldOptions.some((f) => !USER_FIELD_SET.has(f) && !COMPUTED_FIELD_SET.has(f)) && (
                          <optgroup label="Other">
                            {fieldOptions
                              .filter((f) => !USER_FIELD_SET.has(f) && !COMPUTED_FIELD_SET.has(f))
                              .map((f) => (
                                <option key={f} value={f}>{FIELD_LABEL[f] || f}</option>
                              ))}
                          </optgroup>
                        )}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={spec.value}
                        onChange={(e) => updateParam(idx, { value: e.target.value })}
                        placeholder="Same text for every recipient"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview — WhatsApp-style bubble, filled in for a sample recipient */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Preview</p>
              {sampleRecipient && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Sample: <span className="font-medium">{sampleRecipient.full_name
                    || [sampleRecipient.first_name, sampleRecipient.last_name].filter(Boolean).join(' ')
                    || 'recipient'}</span>
                </p>
              )}
            </div>
            {/* Chat backdrop */}
            <div className="rounded-lg p-3 bg-[#e5ddd5] dark:bg-gray-900/60">
              <div className="max-w-[85%] ml-auto rounded-lg rounded-tr-none shadow-sm bg-[#dcf8c6] dark:bg-teal-900/40 overflow-hidden">
                {selectedTemplate.header_text && (
                  <div className="px-3 pt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {selectedTemplate.header_text}
                  </div>
                )}
                <div className="px-3 py-2 text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
                  {renderPreview(selectedTemplate.body_text, value.params, sampleRecipient)}
                </div>
                {selectedTemplate.footer_text && (
                  <div className="px-3 pb-1 text-xs text-gray-500 dark:text-gray-400">
                    {selectedTemplate.footer_text}
                  </div>
                )}
                <div className="px-3 pb-1 text-[10px] text-gray-500 dark:text-gray-400 text-right">
                  12:30 PM ✓✓
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {sampleRecipient
                ? 'Per-user fields show this sample recipient; each person gets their own name at send time. [Brackets] = no sample value available.'
                : 'Fields in [brackets] are filled in per-recipient at send time.'}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// Expose this so callers can map the controlled value back to a backend payload.
export function templatePickerToPayload(value, templates) {
  if (!value.selectedKey) return null;
  const t = templates.find((x) => templateKey(x) === value.selectedKey);
  if (!t) return null;
  return {
    template_name: t.name,
    language: t.language,
    params: value.params,
  };
}
